import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { IconCircleCheckFilled } from "@tabler/icons-react"
import { useTranslation } from "react-i18next"
import { Plan } from "@/content/plans"
import { usePlans } from "@/hooks/use-plans"
import { usePromoCode } from "@/hooks/use-promo-code"
import { formatDiscount } from "@/lib/utils"
import { getDecimalCount } from "@/lib/currencies"

interface PurchaseCardDesktopProps {
  onPurchase: (planId: string) => void;
}

const PurchaseCardDesktop = ({ onPurchase }: PurchaseCardDesktopProps) => {
  const { t, i18n } = useTranslation();
  const { plans, isLoading } = usePlans();
  const { eventCode, referralCode } = usePromoCode();

  // if (isLoading) {
  //   return <div className="hidden lg:block pr-4 xl:pr-8 pl-0 lg:col-span-4">
  //     <div>{t('common.loading')}</div>
  //   </div>;
  // }

  return (
    <div className="hidden lg:block pr-4 xl:pr-8 pl-0 lg:col-span-4 z-30">
      <div className="pt-4 pb-8 top-0 flex flex-col gap-4 overflow-x-visible">
        {plans.map((plan: Plan) => {
          const trialPeriodDays = plan.pricing.trialPeriodDays;

          return (
            <Card key={plan.id} className={`${plan.style.isPopular ? 'shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)]' : ''} border border-slate-200 rounded-2xl bg-white mx-4`}>
              <CardHeader className="text-center p-0">
                <div className={`relative overflow-hidden pb-2 ${plan.style.isPopular ? 'aspect-[16/9]' : 'aspect-[2/1]'}`}>
                  <div className="absolute bg-slate-900 w-full h-full rounded-tl-2xl rounded-tr-2xl"></div>
                  <img
                    src={plan.style.isPopular ?
                      'https://cdn-site.clarymind.com/product-page-app/plans/pro-annual-banner-bg-v2.jpg' :
                      'https://cdn-site.clarymind.com/product-page-app/plans/pro-3_month-banner-bg-v2.jpg'
                    }
                    alt=""
                    className={`absolute inset-0 w-full h-full object-cover rounded-tl-2xl rounded-tr-2xl ${plan.style.isPopular ? "opacity-25" : "opacity-45"}`}
                  />
                  <div className="relative flex flex-col items-start justify-center gap-2 px-8 h-full">
                    {plan.tags && plan.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-1">
                        {plan.tags.map((tag, index) => (
                          <span key={index} className={`${tag.backgroundColor} ${tag.textColor} px-3 py-1 rounded-full text-xs font-semibold`}>
                            {t(tag.text)}
                          </span>
                        ))}
                      </div>
                    )}
                    <CardTitle className={`text-2xl font-semibold text-slate-50 px-1`}>{t(plan.name)}</CardTitle>
                    <div className={`${plan.style.isPopular ? 'bg-slate-50 text-slate-900' : 'border border-slate-50 bg-slate-900/20 backdrop-blur-sm text-slate-50'} px-4 pt-1 pb-1.5 mt-1 rounded-md order-2 md:order-1`}>
                      <span className="text-sm font-semibold">{t(plan.duration.label)}</span>
                    </div>
                    {plan.description && plan.pricing.discount?.percentage !== undefined && (() => {
                      const code = eventCode || 'WEBSITE';
                      const promoKey = `promo.${code}.name`;
                      const promoName = t(promoKey) as string;
                      const discount = formatDiscount(plan.pricing.discount.percentage, i18n.language);
                      const text = promoName !== promoKey ? `${promoName} ${discount}` : t(plan.description, { discount });
                      return (
                        <CardDescription className={`px-1 text-sm font-semibold text-slate-300 transition-all duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                          {text}
                        </CardDescription>
                      );
                    })()}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 p-0 pt-5">
                <div className="flex flex-col items-start justify-center gap-2 px-8 pt-0 pb-3">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className={`text-3xl font-semibold transition-all duration-200 ${plan.style.isPopular ? 'text-red-500' : 'text-slate-800'} ${isLoading ? 'opacity-0' : 'opacity-100'}`}>{plan.pricing.currency} {Number(plan.pricing.current).toLocaleString()}</span>
                    <span className={`text-sm ${plan.style.isPopular ? 'text-red-500' : 'text-slate-800'}`}> / {t(plan.unit)}</span>
                  </div>
                  {plan.pricing.original && (
                    <div className="flex gap-2 mb-0 items-baseline">
                      <span className={`text-sm text-slate-400 line-through transition-all duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>{plan.pricing.currency} {Number(plan.pricing.original).toLocaleString()} / {t(plan.unit)}</span>
                      <span className={`text-sm text-slate-600 transition-all duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                        ({plan.pricing.currency} {Number((plan.pricing.current / plan.duration.months).toFixed(getDecimalCount(plan.pricing.currency))).toLocaleString()} / {t('purchase.perMonth')})
                      </span>
                      {/* <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100">{t(plan.pricing.discount?.label || '') + ' ' + formatDiscount(plan.pricing.discount?.percentage || 0, i18n.language)}</Badge> */}
                    </div>
                  )}

                </div>

                {/* <div className="pt-0 pb-3 px-8">
                  <div className="h-[1px] bg-slate-100"></div>
                </div> */}

                <ul className="space-y-3 px-8 pb-3">
                  {plan.features.map((feature: { text: string; isHighlighted: boolean; isPromo?: boolean }, index: number) => (
                    <li key={index} className="flex items-start gap-2 leading-relaxed">
                      <IconCircleCheckFilled className={`w-6 h-6 ${feature.isPromo ? 'text-green-600' : feature.isHighlighted ? 'text-green-600' : 'text-lime-500'} -mt-0.5 shrink-0`} />
                      <span className={`text-base md:text-sm ${feature.isPromo ? 'text-slate-700 font-semibold' : feature.isHighlighted ? 'text-slate-700' : 'text-slate-500'}`}>{t(feature.text)}</span>
                    </li>
                  ))}
                </ul>

                <div className="px-8 pb-6 flex flex-col items-center justify-center gap-2">
                  <Button
                    onClick={() => {
                      console.log('onPurchase', plan.id);
                      onPurchase(plan.id);
                    }}
                    className={`w-full ${plan.style.buttonStyle.background} ${plan.style.buttonStyle.textColor} ${plan.style.buttonStyle.hoverBackground} border-b-4 ${plan.style.buttonStyle.borderColor} text-base py-6 rounded-lg`}
                  >
                    {trialPeriodDays
                      ? t('purchase.cta.startTrial', { days: trialPeriodDays })
                      : t(plan.cta)}
                  </Button>

                  {/** Referral code savings hint */}
                  {referralCode && (plan as any).pricing?.referralSavings != null && (plan as any).pricing.referralSavings > 0 && (
                    <span className="text-xs text-center text-slate-600 pl-2">
                      {t('common.referralCodeApplied', {
                        code: referralCode,
                        currency: plan?.pricing.currency,
                        amount: Number((plan as any).pricing.referralSavings).toLocaleString()
                      })}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default PurchaseCardDesktop